import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

function KpiCard({ label, value, icon, accent, trend, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="kpi-icon" style={accent ? { background: accent.bg, color: accent.fg } : undefined}>
        <i className={`fa-solid ${icon}`}></i>
      </div>
      <div className="overline text-[10px] mb-3">{label}</div>
      <div className="kpi-number text-5xl text-slate-900">{value}</div>
      {trend && (
        <div className="mt-3 text-[11px] text-slate-500 flex items-center gap-1.5">
          <span
            className="inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded"
            style={{ background: "var(--so-primary-soft)", color: "var(--so-primary)" }}
          >
            <i className="fa-solid fa-arrow-up text-[8px]"></i>{trend.label}
          </span>
          {trend.detail}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.get("/dashboard/summary")
      .then((r) => { if (mounted) setData(r.data); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);

  return (
    <div className="relative">
      {/* Soft watermark background */}
      <div className="absolute inset-0 pointer-events-none so-bg-grid opacity-40"></div>

      <div className="relative p-8 lg:p-12 max-w-7xl mx-auto so-fade-in">
        {/* === Hero header === */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <div className="overline mb-3" style={{ color: "var(--so-primary)" }}>Painel executivo</div>
            <h1 className="font-display text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
              Olá, <span className="so-text-gradient">{user?.name?.split(" ")[0] || "gestor"}</span>.
            </h1>
            <p className="text-slate-600 mt-3 text-[15px] max-w-xl leading-relaxed">
              Visão consolidada das suas avaliações psicossociais, conformidade NR-01 e plano de ação em andamento.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/app/empresas"
              className="btn btn-secondary"
              data-testid="dashboard-companies-link"
            >
              <i className="fa-solid fa-building"></i> Empresas
            </Link>
            <Link
              to="/app/avaliacoes"
              className="btn btn-primary"
              data-testid="dashboard-new-assessment-link"
            >
              <i className="fa-solid fa-plus"></i> Nova avaliação
            </Link>
          </div>
        </div>

        {/* === KPI grid === */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10 so-fade-in-stagger">
          <KpiCard
            testid="kpi-empresas"
            label="Empresas"
            value={data?.companies ?? 0}
            icon="fa-building"
          />
          <KpiCard
            testid="kpi-avaliacoes"
            label="Avaliações"
            value={data?.assessments ?? 0}
            icon="fa-clipboard-list"
            accent={{ bg: "rgba(0,59,92,0.08)", fg: "#003B5C" }}
          />
          <KpiCard
            testid="kpi-respostas"
            label="Respostas"
            value={data?.responses ?? 0}
            icon="fa-users"
            accent={{ bg: "rgba(0,209,160,0.12)", fg: "#0B8C70" }}
          />
          <KpiCard
            testid="kpi-pendentes"
            label="Ações pendentes"
            value={data?.pending_actions ?? 0}
            icon="fa-clock-rotate-left"
            accent={
              (data?.pending_actions ?? 0) > 0
                ? { bg: "#FEF3C7", fg: "#92400E" }
                : { bg: "var(--so-primary-soft)", fg: "var(--so-primary)" }
            }
          />
        </div>

        {/* === Avaliações recentes === */}
        <div className="card so-fade-in" data-testid="recent-assessments-card">
          <div className="flex items-center justify-between mb-5">
            <div>
              <div className="overline mb-1">Avaliações recentes</div>
              <div className="text-sm text-slate-500">Últimos diagnósticos consolidados na plataforma.</div>
            </div>
            <Link
              to="/app/avaliacoes"
              className="text-sm font-semibold inline-flex items-center gap-1.5 hover:gap-2.5 transition-all"
              style={{ color: "var(--so-primary)" }}
              data-testid="recent-see-all"
            >
              Ver todas <i className="fa-solid fa-arrow-right text-xs"></i>
            </Link>
          </div>

          {data?.recent_assessments?.length ? (
            <div className="overflow-x-auto -mx-6 -mb-6">
              <table className="so-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>GHE / Setor</th>
                    <th>Criada em</th>
                    <th className="text-right pr-6">Ação</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent_assessments.map((a) => (
                    <tr key={a.id}>
                      <td className="font-semibold text-slate-900 pl-6">{a.titulo}</td>
                      <td className="text-slate-600">{a.ghe_setor || "—"}</td>
                      <td className="text-slate-500 font-mono text-xs">
                        {new Date(a.created_at).toLocaleDateString("pt-BR")}
                      </td>
                      <td className="text-right pr-6">
                        <Link
                          to={`/app/avaliacoes/${a.id}`}
                          className="inline-flex items-center gap-1.5 font-semibold text-sm hover:gap-2.5 transition-all"
                          style={{ color: "var(--so-primary)" }}
                          data-testid={`dashboard-open-${a.id}`}
                        >
                          Abrir <i className="fa-solid fa-arrow-right text-xs"></i>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-slate-500 py-12 text-center" data-testid="empty-state">
              <div
                className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-4"
                style={{ background: "var(--so-primary-soft)" }}
              >
                <i className="fa-solid fa-clipboard text-2xl" style={{ color: "var(--so-primary)" }}></i>
              </div>
              <div className="font-display text-lg font-bold text-slate-900">Nenhuma avaliação ainda</div>
              <p className="text-sm text-slate-500 mt-1 mb-4">Crie a primeira avaliação para começar.</p>
              <Link
                to="/app/avaliacoes"
                className="btn btn-primary inline-flex"
                data-testid="dashboard-empty-create-link"
              >
                <i className="fa-solid fa-plus"></i> Criar primeira avaliação
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
